import React, { useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";
import DataTable from "../components/ui/DataTable";
import TabBar from "../components/ui/TabBar";
import { statusColor } from "../utils/helpers";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";

const API = "https://maize.norissolutions.in/Php/public/api";

const TABS = [
  { id: "organiser", label: "Organiser Distribution" },
  { id: "farmer", label: "Farmer Distribution" },
];

export default function Distribution() {
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken } = useAuth();

  const [tab, setTab] = useState("organiser");

  const [distributions, setDistributions] = useState([]);
  const [farmerDistributions, setFarmerDistributions] = useState([]);

  const [organisers, setOrganisers] = useState([]);
  const [villages, setVillages] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [fields, setFields] = useState([]);

  const [form, setForm] = useState({
    season_id: "",
    organiser_id: "",
    village_id: "",
    material_id: "",
    quantity_kg: "",
    rate_per_kg: "",
    dist_date: "",
    delivery_note_no: "",
    description: "",
    status: "DELIVERED",
  });

  const [farmerForm, setFarmerForm] = useState({
    distribution_id: "",
    farmer_id: "",
    field_id: "",
    season_id: "",
    quantity_kg: "",
    issue_date: "",
    return_due_date: "",
    expected_return_kg: "",
    notes: "",
  });

  /* ================= FETCH APIs ================= */

  const fetchDistributions = async () => {
    const res = await axios.get(`${API}/distributions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setDistributions(res.data?.data || []);
  };

  const fetchFarmerDistributions = async () => {
    const res = await axios.get(`${API}/farmer-distributions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setFarmerDistributions(res.data?.data || []);
  };

  const fetchFarmers = async () => {
    const res = await axios.get(`${API}/farmers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setFarmers(res.data?.data || []);
  };

  const fetchFields = async (farmerId) => {
    const res = await axios.get(`${API}/farmers/${farmerId}/fields`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const fieldData = res.data?.data || [];
    setFields(fieldData);

    if (fieldData.length > 0) {
      setFarmerForm((prev) => ({
        ...prev,
        field_id: fieldData[0].field_id,
      }));
    }
  };

  const fetchOrganisers = async () => {
    const res = await axios.get(`${API}/organisers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setOrganisers(res.data?.data || []);
  };

  const fetchVillages = async () => {
    const res = await axios.get(`${API}/villages`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setVillages(res.data?.data || []);
  };

  const fetchMaterials = async () => {
    const res = await axios.get(`${API}/materials`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setMaterials(res.data?.data || []);
  };

  const fetchSeasons = async () => {
    const res = await axios.get(`${API}/seasons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setSeasons(res.data?.data || []);
  };

  useEffect(() => {
    fetchDistributions();
    fetchFarmerDistributions();
    fetchFarmers();
    fetchOrganisers();
    fetchVillages();
    fetchMaterials();
    fetchSeasons();
  }, []);

  /* ================= CREATE DISTRIBUTION ================= */

  const handleSubmit = async () => {
    const payload = {
      season_id: Number(form.season_id),
      organiser_id: Number(form.organiser_id),
      village_id: Number(form.village_id),
      material_id: Number(form.material_id),
      dist_date: form.dist_date,
      quantity_kg: Number(form.quantity_kg),
      rate_per_kg: Number(form.rate_per_kg),
      status: form.status,
      delivery_note_no: form.delivery_note_no,
      description: form.description,
    };

    await axios.post(`${API}/distributions`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchDistributions();

    setForm({
      season_id: "",
      organiser_id: "",
      village_id: "",
      material_id: "",
      quantity_kg: "",
      rate_per_kg: "",
      dist_date: "",
      delivery_note_no: "",
      description: "",
      status: "DELIVERED",
    });
  };

  /* ================= CREATE FARMER DISTRIBUTION ================= */

  const handleFarmerDistribution = async () => {
    const payload = {
      distribution_id: Number(farmerForm.distribution_id),
      farmer_id: Number(farmerForm.farmer_id),
      field_id: Number(farmerForm.field_id),
      season_id: Number(farmerForm.season_id),
      quantity_kg: Number(farmerForm.quantity_kg),
      issue_date: farmerForm.issue_date,
      return_due_date: farmerForm.return_due_date,
      expected_return_kg: Number(farmerForm.expected_return_kg),
      notes: farmerForm.notes,
    };

    await axios.post(`${API}/farmer-distributions`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchFarmerDistributions();
  };

  /* ================= TABLE COLUMNS ================= */

  const DISTRIBUTION_COLUMNS = [
    { key: "distribution_id", label: "Dist ID" },
    { key: "organiser_name", label: "Organiser" },
    { key: "material_name", label: "Material" },
    { key: "quantity_kg", label: "Qty" },
    { key: "dist_date", label: "Date" },
    {
      key: "status",
      label: "Status",
      render: (v) => <Badge color={statusColor(v)}>{v}</Badge>,
    },
  ];

  const FARMER_DIST_COLUMNS = [
    { key: "farmer_name", label: "Farmer" },
    { key: "field_name", label: "Field" },
    { key: "quantity_kg", label: "Qty" },
    { key: "issue_date", label: "Issue Date" },
    { key: "expected_return_kg", label: "Expected Return" },
  ];

  return (
    <div>
      <PageHeader title="Distribution">
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === "organiser" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <Card title="Distribute to Organiser">
            <div className="form-grid--three">

              <FormField
                label="Season"
                type="select"
                options={seasons.map((s) => ({
                  label: s.season_name,
                  value: s.season_id,
                }))}
                value={form.season_id}
                onChange={(e) =>
                  setForm({ ...form, season_id: e.target.value })
                }
              />

              <FormField
                label="Organiser"
                type="select"
                options={organisers.map((o) => ({
                  label: o.organiser_name,
                  value: o.organiser_id,
                }))}
                value={form.organiser_id}
                onChange={(e) =>
                  setForm({ ...form, organiser_id: e.target.value })
                }
              />

              <FormField
                label="Village"
                type="select"
                options={villages.map((v) => ({
                  label: v.village_name,
                  value: v.village_id,
                }))}
                value={form.village_id}
                onChange={(e) =>
                  setForm({ ...form, village_id: e.target.value })
                }
              />

              <FormField
                label="Material"
                type="select"
                options={materials.map((m) => ({
                  label: m.material_name,
                  value: m.material_id,
                }))}
                value={form.material_id}
                onChange={(e) =>
                  setForm({ ...form, material_id: e.target.value })
                }
              />

              <FormField
                label="Quantity"
                type="number"
                value={form.quantity_kg}
                onChange={(e) =>
                  setForm({ ...form, quantity_kg: e.target.value })
                }
              />

              <FormField
                label="Rate per KG"
                type="number"
                value={form.rate_per_kg}
                onChange={(e) =>
                  setForm({ ...form, rate_per_kg: e.target.value })
                }
              />

              <FormField
                label="Distribution Date"
                type="date"
                value={form.dist_date}
                onChange={(e) =>
                  setForm({ ...form, dist_date: e.target.value })
                }
              />

              <FormField
                label="Delivery Note No"
                type="text"
                value={form.delivery_note_no}
                onChange={(e) =>
                  setForm({ ...form, delivery_note_no: e.target.value })
                }
              />

              <FormField
                label="Description"
                type="textarea"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>

            <Button style={{ marginTop: 14 }} onClick={handleSubmit}>
              Issue Distribution
            </Button>
          </Card>

          <Card title="Distribution Log" noPad>
            <DataTable columns={DISTRIBUTION_COLUMNS} rows={distributions} />
          </Card>
        </div>
      )}

      {tab === "farmer" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <Card title="Distribute to Farmer">
            <div className="form-grid--three">

              <FormField
                label="Distribution"
                type="select"
                options={distributions.map((d) => ({
                  label: d.delivery_note_no,
                  value: d.distribution_id,
                }))}
                value={farmerForm.distribution_id}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    distribution_id: e.target.value,
                  })
                }
              />

              <FormField
                label="Farmer"
                type="select"
                options={farmers.map((f) => ({
                  label: f.farmer_name,
                  value: f.farmer_id,
                }))}
                value={farmerForm.farmer_id}
                onChange={(e) => {
                  const farmerId = e.target.value;

                  setFarmerForm({
                    ...farmerForm,
                    farmer_id: farmerId,
                    field_id: "",
                  });

                  fetchFields(farmerId);
                }}
              />

              <FormField
                label="Field"
                type="select"
                options={fields.map((f) => ({
                  label: f.field_name,
                  value: f.field_id,
                }))}
                value={farmerForm.field_id}
                onChange={(e) =>
                  setFarmerForm({ ...farmerForm, field_id: e.target.value })
                }
              />

              <FormField
                label="Season"
                type="select"
                options={seasons.map((s) => ({
                  label: s.season_name,
                  value: s.season_id,
                }))}
                value={farmerForm.season_id}
                onChange={(e) =>
                  setFarmerForm({ ...farmerForm, season_id: e.target.value })
                }
              />

              <FormField
                label="Quantity (KG)"
                type="number"
                value={farmerForm.quantity_kg}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    quantity_kg: e.target.value,
                  })
                }
              />

              <FormField
                label="Issue Date"
                type="date"
                value={farmerForm.issue_date}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    issue_date: e.target.value,
                  })
                }
              />

              <FormField
                label="Return Due Date"
                type="date"
                value={farmerForm.return_due_date}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    return_due_date: e.target.value,
                  })
                }
              />

              <FormField
                label="Expected Return (KG)"
                type="number"
                value={farmerForm.expected_return_kg}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    expected_return_kg: e.target.value,
                  })
                }
              />

              <FormField
                label="Notes"
                type="textarea"
                value={farmerForm.notes}
                onChange={(e) =>
                  setFarmerForm({
                    ...farmerForm,
                    notes: e.target.value,
                  })
                }
              />
            </div>

            <Button
              style={{ marginTop: 14 }}
              onClick={handleFarmerDistribution}
            >
              Issue to Farmer
            </Button>
          </Card>

          <Card title="Farmer Distribution Log" noPad>
            <DataTable columns={FARMER_DIST_COLUMNS} rows={farmerDistributions} />
          </Card>
        </div>
      )}
    </div>
  );
}